import { ACCENT, AMBER, BG, BORDER, CARD, DARK, DIM, MID } from "../theme";
import { AIAnswer } from "../aiFormat";
import { AI_ANALYST_INTRO, todayLine } from "../aiPersona";
import { AI_DISCLAIMER } from "../aiDisclaimers";
import { AI_SUGGESTIONS, aiGreeting } from "../aiGreeting";
import { DEMO_AI_FREE_TEXT_REPLY, demoAnswerFor } from "../demoMode";
import { Fragment, useEffect, useRef, useState } from "react";
import { budgetLine, budgetState, keepUsedUp, parseCanopyUsage, usedUpFromResponse, usedUpMessage } from "../aiBudget";
import { buildSystemPrompt, trimHistoryForSend } from "../aiChatHistory";
import { dataQualityBannerText, dataQualityInstructions, dataQualityPromptSection, periodPhrase, periodScreenLine, summarizeDataQuality } from "../aiDataQuality";
import { PLACEHOLDER_INSTRUCTION, collectNames, createPrivacy } from "../aiPrivacy";
import { getAccessToken } from "../lib/supabase";
import { isDemoSource } from "../dataState";
import { jobCompleteness, marginView } from "../jobCompleteness";
import { useBusinessType } from "../useBusinessType";
import { monthlyTotalsForAI, undatedNoteForAI } from "../aiCharts";
import { useDevice, useIsMobile } from "../hooks/useDevice";

// ─── TAB: AI CHAT ─────────────────────────────────────────────────────────────

function AIChat({ businessName, jobSummaries, trendData, needsReview, qualityJobs, dateRange, dataSource, onOpenExpenses, demoAnswerList }) {
  const isDemo = isDemoSource(dataSource); // the public demo answers from written answers; no server calls, no budget
  const { rule } = useBusinessType();
  const isMobile = useIsMobile();
  const isPhone = useDevice() === "phone";
  const [bannerOpen, setBannerOpen] = useState(false); // phone only: the warning shows one line until opened
  const quality = summarizeDataQuality({ jobSummaries: qualityJobs || jobSummaries, needsReview, laborCounts: rule.missingLaborCounts, noCostsCounts: rule.noCostsWithholds });
  const period = periodPhrase(dateRange);
  const banner = dataQualityBannerText(quality, period);
  // Monthly totals come from the same tested code as the charts (src/aiCharts.js), so the AI's words match them.
  const trend = trendData || monthlyTotalsForAI(jobSummaries);
  const undatedNote = trendData ? "" : undatedNoteForAI(jobSummaries);

  // Names are replaced with labels before anything goes to the AI, and put back in its answers
  // (src/aiPrivacy.js). The mapping lives in this chat's memory only: never sent, stored or logged.
  const privacyRef = useRef(null);
  if (!privacyRef.current) privacyRef.current = createPrivacy();

  function buildPrompt(privacy) {
    privacy.register(collectNames({ jobs: jobSummaries, needsReview, businessName }));
    const p = privacy.pseudonymize;
    return buildSystemPrompt(
      AI_ANALYST_INTRO + "\n\n" + [todayLine(new Date()), PLACEHOLDER_INSTRUCTION, dataQualityInstructions(quality, dataSource, !!onOpenExpenses), periodScreenLine(dateRange), p(dataQualityPromptSection(quality, period)), undatedNote].filter(Boolean).join("\n"),
      // Only the name and client are replaced. Type, status and the missing-data sentences are category labels.
      jobSummaries.map(j=>{ const c = jobCompleteness(j, [], rule); return { name:p(j.name),client:p(j.clientName),type:j.type,status:j.status,revenue:j.revenue,costs:j.costs,profit:j.profit,marginPct:marginView(j, c).text,missing:c.reasons,lastDate:j.lastDate }; }),
      trend
    );
  }

  const [messages, setMessages] = useState([
    { role:"assistant", content:aiGreeting(quality, isDemo) }
  ]);
  const [input, setInput]   = useState("");
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);   // the AI budget; null means we could not load it, so show nothing
  const bottomRef = useRef(null);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages,loading]);
  useEffect(() => {
    if (isDemo) return undefined;
    let alive = true;
    (async () => {
      try {
        const token = await getAccessToken();
        if (!token) return;
        const res = await fetch("/api/ai-usage", { headers:{ Authorization:`Bearer ${token}` } });
        if (!res.ok) return;
        const data = await res.json().catch(() => ({}));
        const u = parseCanopyUsage(data?.canopy_usage);
        // A used-up answer from the chat may arrive first; this late result must not undo it.
        if (alive && u) setUsage(prev => keepUsedUp(prev, u));
      } catch (e) { /* no budget line is fine; the chat still works */ }
    })();
    return () => { alive = false; };
  }, [isDemo]);
  const budget = budgetState(usage);
  const budgetUsedUp = budget === "used_up";

  const SUGGESTIONS = isDemo ? (demoAnswerList || []).map(a => a.question) : AI_SUGGESTIONS;

  async function send(text) {
    const msg = text || input.trim();
    if (!msg || loading || budgetUsedUp) return;
    setInput("");
    const newMessages = [...messages, { role:"user",content:msg }];
    setMessages(newMessages);
    if (isDemo) {
      // Written answers worked out from the demo numbers. Nothing is sent anywhere.
      const answer = demoAnswerFor(demoAnswerList, msg);
      setMessages([...newMessages, { role:"assistant", content: answer || DEMO_AI_FREE_TEXT_REPLY }]);
      return;
    }
    setLoading(true);
    try {
      const privacy = privacyRef.current;
      const system = buildPrompt(privacy);
      // Earlier messages go back in label form too. The screen keeps the real names.
      const toSend = trimHistoryForSend(newMessages.map(m => ({ ...m, content: privacy.pseudonymize(m.content) })));
      const token = await getAccessToken();
      const res = await fetch("/api/ai-chat", {
        method:"POST",
        headers:{ "Content-Type":"application/json", ...(token ? { Authorization:`Bearer ${token}` } : {}) },
        body:JSON.stringify({ system, max_tokens:1000, messages:toSend, names_removed:true })
      });
      const data = await res.json().catch(() => ({}));
      const usedUp = usedUpFromResponse(res.status, data);   // a 429 with budget numbers; a plain 429 is "busy"
      const fresh = usedUp || parseCanopyUsage(data?.canopy_usage);
      if (fresh) setUsage(fresh);
      const text = res.ok ? privacy.restore(data.content?.map(b=>b.text||"").join("") || "") : "";
      const reply = res.status === 401 ? "Please sign in again to use the AI assistant."
        : usedUp ? usedUpMessage(usedUp)
        : !res.ok ? (data.error || "Sorry, couldn't get a response.")
        : text || "Sorry, couldn't get a response.";
      setMessages([...newMessages, { role:"assistant",content:reply,isError:!res.ok || !text }]);
    } catch(e) {
      setMessages([...newMessages, { role:"assistant",content:"Error connecting to Claude API. Make sure you're running this with API access.",isError:true }]);
    }
    setLoading(false);
  }

  return (
    <div className="r-chat" style={{ display:"flex",flexDirection:"column",height: isMobile ? "100dvh" : "calc(100vh - 56px)",padding: isMobile ? "0 16px 90px" : "0 36px 28px",background:BG }}>
      <div className="r-chat-head" style={{ paddingTop:28,paddingBottom:18,borderBottom:`1px solid ${BORDER}`,marginBottom:22 }}>
        <h1 style={{ fontFamily:"'Lora',serif",fontSize:24,fontWeight:600,color:DARK,letterSpacing:"-0.02em",marginBottom:4 }}>AI Business Analyst</h1>
        <p style={{ fontFamily:"'DM Sans',sans-serif",fontSize:13,color:DIM }}>Ask anything about your jobs, margins, or trends — in plain English.</p>
      </div>
      {banner && (
        <div role="status" style={{ flexShrink:0,marginBottom:14,padding:"9px 14px",borderRadius:5,border:`1px solid rgba(140,107,48,0.3)`,background:"rgba(140,107,48,0.05)",fontSize:12,fontFamily:"'DM Sans',sans-serif",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
          <div style={{ flex:"1 1 220px",minWidth:0,lineHeight:1.5 }}>
            <span style={{ fontWeight:500,color:AMBER }}>{banner.title}</span>{(!isPhone || bannerOpen) && <span style={{ color:MID,marginLeft:8 }}>{banner.body}</span>}
          </div>
          {isPhone && (
            <button type="button" aria-expanded={bannerOpen} onClick={()=>setBannerOpen(o=>!o)} style={{ cursor:"pointer",minHeight:44,padding:"0 10px",background:"none",border:"none",color:AMBER,textDecoration:"underline",fontSize:12,fontFamily:"'DM Sans',sans-serif" }}>{bannerOpen ? "Hide details" : "Details"}</button>
          )}
          {(!isPhone || bannerOpen) && onOpenExpenses && quality.unassignedCount > 0 && (
            <button onClick={onOpenExpenses} style={{ cursor:"pointer",padding:"6px 12px",borderRadius:4,fontSize:12,border:`1px solid ${AMBER}`,color:AMBER,background:"transparent",fontFamily:"'DM Sans',sans-serif",whiteSpace:"nowrap" }}>Sort expenses</button>
          )}
        </div>
      )}
      <div style={{ flex:1,minHeight:0,overflowY:"auto",display:"flex",flexDirection:"column",gap:14,paddingRight:6 }}>
        {messages.length === 1 ? (
          <div style={{ flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:"32px 0",gap:20 }}>
            <div className="r-card-pad" style={{ maxWidth:540,width:"100%",padding:"22px 26px",borderRadius:8,border:`1px solid ${BORDER}`,background:CARD,fontFamily:"'DM Sans',sans-serif",fontSize:13,color:MID,lineHeight:1.75 }}>
              {messages[0].content}
            </div>
            <div style={{ display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center",maxWidth:540 }}>
              {SUGGESTIONS.map((s,i) => (
                <button key={i} disabled={budgetUsedUp} onClick={()=>send(s)} style={{ cursor:budgetUsedUp?"not-allowed":"pointer",opacity:budgetUsedUp?0.5:1,padding:"9px 16px",borderRadius:4,fontSize:12,border:`1px solid ${BORDER}`,color:MID,background:CARD,fontFamily:"'DM Sans',sans-serif",transition:"all 0.15s",boxShadow:"0 1px 2px rgba(44,36,22,0.05)" }}
                  onMouseOver={e=>{if(budgetUsedUp)return;e.currentTarget.style.borderColor=ACCENT;e.currentTarget.style.color=ACCENT;}}
                  onMouseOut={e=>{e.currentTarget.style.borderColor=BORDER;e.currentTarget.style.color=MID;}}
                >{s}</button>
              ))}
            </div>
          </div>
        ) : (
          <>
            {messages.map((m,i) => (
              <Fragment key={i}>
              {m.role==="assistant" && !m.isError
                ? <div className="chat-bubble-ai"><AIAnswer text={m.content} jobs={jobSummaries}/></div>
                : <div className={m.role==="user"?"chat-bubble-user":"chat-bubble-ai"} style={{ whiteSpace:"pre-wrap" }}>{m.content}</div>}
              {m.role==="assistant" && !m.isError && i>0 && <div style={{ fontSize:11,color:DIM,margin:"-6px 0 12px 4px" }}>{AI_DISCLAIMER}</div>}
              </Fragment>
            ))}
            {loading && <div className="chat-bubble-ai"><div className="thinking"><span/><span/><span/></div></div>}
          </>
        )}
        <div ref={bottomRef}/>
      </div>
      <div className="r-chat-input" style={{ display:"flex",gap:10,marginTop:18 }}>
        <input className="chat-input" maxLength={8000} value={input} disabled={budgetUsedUp} onChange={e=>setInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&send()} placeholder={budgetUsedUp ? usedUpMessage(usage) : isDemo ? "Pick a suggested question" : "Ask about your jobs, margins, costs..."}/>
        <button className="btn act" disabled={budgetUsedUp} onClick={()=>send()} style={{ whiteSpace:"nowrap",padding:"11px 22px",...(budgetUsedUp ? { opacity:0.5,cursor:"not-allowed" } : {}) }}>Send</button>
      </div>
      {budgetLine(usage) && (
        <div role="status" style={{ fontSize:11,fontFamily:"'DM Sans',sans-serif",color: budget==="ok" ? DIM : AMBER,marginTop:8 }}>{budgetLine(usage)}</div>
      )}
    </div>
  );
}

export { AIChat };
